import { reactive } from "vue";
import { api } from "@/api";

/**
 * Decode JWT payload (sans vérifier signature)
 */
function decodeJwt(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload));
  } catch (e) {
    return null;
  }
}

/**
 * True si expiré (avec marge de sécurité)
 */
function isJwtExpired(token, skewSeconds = 30) {
  const p = decodeJwt(token);
  if (!p?.exp) return true;
  const expMs = (p.exp - skewSeconds) * 1000;
  return Date.now() >= expMs;
}

export const userState = reactive({
  accessToken: null,
  refreshToken: null,
  me: null,

  _refreshPromise: null,
  _interceptorsReady: false,

  globalSaving: false,
  menuRevision: 0,

  isLoggedIn() {
    // connecté si tokens présents + user chargé
    return (
      this.accessToken !== null &&
      this.refreshToken !== null &&
      this.me !== null
    );
  },

  clear() {
    this.me = null;
    this.accessToken = null;
    this.refreshToken = null;

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    // Nettoyer header global
    delete api.defaults.headers.common["Authorization"];
  },

  setMe(me) {
    this.me = me;
  },

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;

    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);

    api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
  },

  isRefreshValid() {
    return !!this.refreshToken && !isJwtExpired(this.refreshToken);
  },

  isAccessValid() {
    return !!this.accessToken && !isJwtExpired(this.accessToken);
  },

  /**
   * Installe interceptors (1 seule fois)
   * - Ajoute Authorization
   * - Sur 401 : refresh + retry
   * - Si refresh impossible : clear + redirect /connection
   */
  _setupAuthInterceptors() {
    if (this._interceptorsReady) return;
    this._interceptorsReady = true;

    // Request interceptor : met le token si présent
    api.interceptors.request.use((config) => {
      const t = this.accessToken || localStorage.getItem("accessToken");
      if (t) {
        config.headers = config.headers || {};
        config.headers.Authorization = "Bearer " + t;
      }
      return config;
    });

    // Response interceptor : refresh sur 401
    api.interceptors.response.use(
      (res) => res,
      async (error) => {
        const status = error?.response?.status;
        const original = error?.config || {};

        // si 401 et pas déjà retry
        if (status === 401 && !original._retry) {
          original._retry = true;

          try {
            await this.ensureValidAccessToken();
            original.headers = original.headers || {};
            original.headers.Authorization = "Bearer " + this.accessToken;
            return api(original); // retry request
          } catch (e) {
            this.clear();
            // Redirection hard (marche même si Vue/router est dans un état bizarre)
            if (!window.location.pathname.startsWith("/connection")) {
              window.location.href = "/connection";
            }
          }
        }

        return Promise.reject(error);
      }
    );
  },

  /**
   * Garantit un accessToken valide :
   * - si access ok => return
   * - si access expiré mais refresh ok => refresh
   * - sinon => clear + throw
   */
  async ensureValidAccessToken() {
    if (this.isAccessValid()) return this.accessToken;

    if (!this.isRefreshValid()) {
      this.clear();
      throw new Error("Refresh token expired/invalid");
    }

    // évite refresh multiple en parallèle
    if (!this._refreshPromise) {
      this._refreshPromise = this.refreshAccessToken().finally(() => {
        this._refreshPromise = null;
      });
    }

    await this._refreshPromise;
    return this.accessToken;
  },

  /**
   * Remplace ton ancien recoverAuth :
   * - charge tokens
   * - setup interceptors
   * - refresh si besoin
   * - charge /me
   */
  async recoverAuth() {
    this._setupAuthInterceptors();

    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!accessToken || !refreshToken) {
      this.clear();
      throw new Error("No tokens in localStorage");
    }

    this.setTokens(accessToken, refreshToken);

    // si access expiré mais refresh valide => on refresh
    await this.ensureValidAccessToken();

    // puis on récupère le user
    const meRes = await api.get("/me/");
    this.me = meRes.data;

    return meRes.data;
  },

  /**
   * Login corrigé :
   * - pas de resolve() avant /me
   * - setup interceptors
   */
  async login(email, password) {
    this._setupAuthInterceptors();

    const response = await api.post("/auth/token/", { email, password });

    this.setTokens(response.data.access, response.data.refresh);

    const meRes = await api.get("/me/");
    this.me = meRes.data;

    return meRes.data;
  },

  logout() {
    this.clear();
  },

  /**
   * Ancien loadTokensFromLocalStorage => devient un alias propre
   * (si tu l’utilises ailleurs)
   */
  async loadTokensFromLocalStorage() {
    return this.recoverAuth();
  },

  /**
   * Refresh token : endpoint existant chez toi
   * Attention : parfois l'API ne renvoie PAS de refresh => on garde l'ancien
   */
  async refreshAccessToken() {
    const response = await api.post("/auth/token/refresh/", {
      refresh: this.refreshToken,
    });

    const newAccessToken = response.data.access;
    const newRefreshToken = response.data.refresh || this.refreshToken;

    if (!newAccessToken) throw new Error("No access returned by refresh");

    this.setTokens(newAccessToken, newRefreshToken);
    return newAccessToken;
  },

  // -----------------------------
  // Le reste de tes méthodes inchangé
  // -----------------------------

  resetPassword(email) {
    return new Promise((resolve, reject) => {
      api.post("/auth/reset_password/", { email })
        .then(() => resolve())
        .catch((error) => reject(error));
    });
  },

  savePreferences(preferences, options = {}) {
    return new Promise((resolve, reject) => {
      const regenerate = options.regenerate === false ? "?regenerate=false" : "";
      api.patch(`/my-preferences/${regenerate}`, preferences, { timeout: options.timeout || 15000 })
        .then((response) => {
          const consumerAccount = response?.data?.consumer_account;
          if (response.data?.success !== true || !consumerAccount) {
            throw new Error("Le serveur n’a pas confirmé la sauvegarde.");
          }
          const canonical = value => JSON.stringify(value, (key, item) => item && !Array.isArray(item) && typeof item === "object" ? Object.keys(item).sort().reduce((result,key)=>{result[key]=item[key];return result;},{}) : item);
          const fieldsNotSaved = Object.keys(preferences).filter(key => {
            if (key === "excluded_aliments") {
              const ids = list => (list || []).map(item => item.id).sort((a,b)=>a-b);
              return JSON.stringify(ids(preferences[key])) !== JSON.stringify(ids(consumerAccount[key]));
            }
            return canonical(preferences[key]) !== canonical(consumerAccount[key]);
          });
          if (fieldsNotSaved.length) {
            const error = new Error("Certains réglages n’ont pas été enregistrés.");
            error.response = {data:{detail:"Certains réglages n’ont pas été enregistrés : " + fieldsNotSaved.join(", ")}};
            throw error;
          }

          if (consumerAccount && this.me) {
            this.me.consumer_account = consumerAccount;
          } else if (this.me?.consumer_account) {
            this.me.consumer_account = {
              ...this.me.consumer_account,
              ...preferences,
            };
          }

          resolve(response.data);
        })
        .catch((error) => reject(error));
    });
  },

  updateConsumer(id, data) {
    this.me.consumer_account.consumers =
      this.me.consumer_account.consumers.map((consumer) => {
        if (consumer.id === id) return data;
        return consumer;
      });
  },

  toggleEmailEnabled() {
    return new Promise((resolve, reject) => {
      api.post("/me/toggle_email_enabled/")
        .then((response) => {
          this.me.email_enabled = response.data.email_enabled;
          resolve();
        })
        .catch((error) => reject(error));
    });
  },

  unsubsribe(email) {
    return new Promise((resolve, reject) => {
      api.post("/unsubscribe/", { email })
        .then(() => resolve())
        .catch((error) => reject(error));
    });
  },

  deleteAccount() {
    return new Promise((resolve, reject) => {
      api.post("/me/delete_account/")
        .then(() => resolve())
        .catch((error) => reject(error));
    });
  },

  changePassword(newPassword) {
    return new Promise((resolve, reject) => {
      api.post("/auth/change_password/", { password: newPassword })
        .then(() => resolve())
        .catch((error) => reject(error));
    });
  },

  updateEmail(newEmail) {
    return new Promise((resolve, reject) => {
      api.patch("/me/", { email: newEmail })
        .then((response) => {
          this.me.email = newEmail;
          resolve(response);
        })
        .catch((error) => reject(error));
    });
  },
});
